import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { useApolloClient, useMutation } from '@apollo/client';
import { memberfulQueries } from '../../../graphql/queries';
import mutations from '../../../graphql/mutations.js';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 700,
  maxHeight: '80vh',
  overflow: 'auto',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 1
};

export const formatBillingDate = (unixTimestamp) => {
  if (!unixTimestamp) return '-';
  return new Date(unixTimestamp * 1000).toLocaleDateString();
};

const getStatusChip = (mapping) => {
  if (mapping.skipReason === 'no_match') {
    return <Chip label='No match' color='error' size='small' />;
  }
  if (mapping.skipReason === 'free_trial') {
    return <Chip label='Free trial (skip)' color='default' size='small' />;
  }
  if (mapping.skipReason === 'already_active') {
    return <Chip label='Already active' color='info' size='small' />;
  }
  if (mapping.willSkip) {
    return <Chip label='Will skip' color='default' size='small' />;
  }
  if (mapping.isFreeTrial) {
    return <Chip label='Will migrate (trial)' color='warning' size='small' />;
  }
  return <Chip label='Will migrate' color='success' size='small' />;
};

const MemberfulPreviewModal = ({ open, email, onClose, onMigrated }) => {
  const [migrationResult, setMigrationResult] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [includeTrials, setIncludeTrials] = useState(false);

  const client = useApolloClient();
  const [migrateMember, { loading: migrateLoading }] = useMutation(mutations.MigrateMemberfulMember);

  useEffect(() => {
    if (open && email) {
      setMigrationResult(null);
      setData(null);
      setError(null);
      setLoading(true);
      client.query({
        query: memberfulQueries.MemberfulMigrationPreview(email, includeTrials),
        fetchPolicy: 'no-cache'
      }).then(result => {
        setData(result.data);
        setLoading(false);
      }).catch(err => {
        setError(err);
        setLoading(false);
      });
    }
  }, [open, email, includeTrials]);

  const handleMigrate = async () => {
    try {
      const { data: result } = await migrateMember({ variables: { email, includeTrials } });
      setMigrationResult(result.migrateMemberfulMember);
      if (onMigrated) onMigrated();
    } catch (err) {
      setMigrationResult({ status: 'failed', errors: [err.message] });
    }
  };

  const handleClose = () => {
    setMigrationResult(null);
    setIncludeTrials(false);
    onClose();
  };

  const preview = data ? data.memberfulMigrationPreview : null;
  const hasFreeTrial = preview && preview.subscriptionMappings.some(m => m.isFreeTrial);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        <Typography variant='h6' sx={{ mb: 2 }}>
          Migration Preview
        </Typography>

        {loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant='body2' sx={{ mt: 1 }}>Loading preview...</Typography>
          </Box>
        )}

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error.message}
          </Alert>
        )}

        {migrationResult && (
          <Alert
            severity={migrationResult.status === 'success' ? 'success' : 'error'}
            sx={{ mb: 2 }}
          >
            Migration {migrationResult.status}
            {migrationResult.subscriptionsMigrated && migrationResult.subscriptionsMigrated.length > 0 && (
              <span>
                {' - '}Migrated {migrationResult.subscriptionsMigrated.length} subscription(s)
              </span>
            )}
            {migrationResult.errors && migrationResult.errors.length > 0 && (
              <span>
                {' - '}Errors: {migrationResult.errors.join(', ')}
              </span>
            )}
          </Alert>
        )}

        {preview && !migrationResult && (
          <>
            <Typography variant='subtitle2' color='textSecondary'>Memberful Member</Typography>
            <Typography variant='body1'>
              {preview.memberfulMember.fullName} ({preview.memberfulMember.email})
            </Typography>
            {preview.memberfulMember.stripeCustomerId && (
              <Typography variant='body2' color='textSecondary'>
                Stripe: {preview.memberfulMember.stripeCustomerId}
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant='subtitle2' color='textSecondary'>BridgeBee User</Typography>
            {preview.existingBBUser && (
              <Typography variant='body1'>
                Existing user: {preview.existingBBUser.firstName} {preview.existingBBUser.lastName}
                {' '}(ID: {preview.existingBBUser.id})
              </Typography>
            )}
            {!preview.existingBBUser && (
              <Typography variant='body1' color='success.main'>
                New user will be created
              </Typography>
            )}
            <Chip
              label={preview.action === 'create' ? 'CREATE' : 'UPDATE'}
              color={preview.action === 'create' ? 'success' : 'primary'}
              size='small'
              sx={{ mt: 0.5 }}
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant='subtitle2' color='textSecondary' sx={{ mb: 1 }}>
              Subscription Mappings
            </Typography>

            <TableContainer component={Paper} variant='outlined'>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Memberful Plan</TableCell>
                    <TableCell>BB Subscription</TableCell>
                    <TableCell>Plan Type</TableCell>
                    <TableCell>Next Billing Date</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.subscriptionMappings.map((mapping, index) => (
                    <TableRow key={index}>
                      <TableCell>{mapping.memberfulPlan.name}</TableCell>
                      <TableCell>
                        {mapping.bbSubscription
                          ? mapping.bbSubscription.title
                          : <Typography variant='body2' color='error'>No match</Typography>}
                      </TableCell>
                      <TableCell>
                        {mapping.bbSubscriptionPlan ? mapping.bbSubscriptionPlan.type : '-'}
                      </TableCell>
                      <TableCell>
                        {formatBillingDate(mapping.expiresAt)}
                      </TableCell>
                      <TableCell>
                        {getStatusChip(mapping)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {preview.warnings && preview.warnings.length > 0 && (
              <Box sx={{ mt: 2 }}>
                {preview.warnings.map((warning, index) => (
                  <Alert key={index} severity='warning' sx={{ mb: 1 }}>
                    {warning}
                  </Alert>
                ))}
              </Box>
            )}

            {hasFreeTrial && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'warning.main',
                  borderRadius: 1,
                  bgcolor: '#fff8e1'
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeTrials}
                      onChange={(e) => setIncludeTrials(e.target.checked)}
                      color='warning'
                    />
                  }
                  label={
                    <Typography variant='body2'>
                      <strong>Testing only:</strong> migrate free trials. Creates a live
                      Stripe subscription; the card is charged when the trial ends.
                    </Typography>
                  }
                />
              </Box>
            )}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button variant='outlined' onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant='contained'
                color='primary'
                onClick={handleMigrate}
                disabled={migrateLoading}
              >
                {migrateLoading ? <CircularProgress size={24} /> : 'Confirm Migration'}
              </Button>
            </Box>
          </>
        )}

        {migrationResult && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant='outlined' onClick={handleClose}>
              Close
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

MemberfulPreviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  email: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onMigrated: PropTypes.func
};

export default MemberfulPreviewModal;
